import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { Icon } from "../../../../components/icon/icon";
import { Pill } from "../../../../components/pill";
import { RenderMarkdown } from "../../../../components/typography/renderMarkdown";
import { CreatedTicket } from "../../../../redux/features/event/event.types";
import {
  MAX_PURCHASE_QUANTITY,
  WARNING_TICKET_QUANTITY,
} from "../../../../utils/constants";
import { formatToCurrency, getCurrencyShort } from "../../../../utils/helpers";
import { TicketPurchase } from "../../../../utils/types";

export const ChooseTickets = ({
  tickets,
  ticketsPurchase,
  setTicketsPurchase,
  editableView = false,
  isEdit = false,
  minPurchaseQuantity = 0,
  showControls = true,
  liveQuantityTracking = false,
}: {
  tickets: CreatedTicket[];
  ticketsPurchase: TicketPurchase[];
  setTicketsPurchase: (data: TicketPurchase[]) => void;
  editableView?: boolean;
  isEdit?: boolean;
  minPurchaseQuantity?: number;
  showControls?: boolean;
  liveQuantityTracking?: boolean; // this is for when ticket quantity is being tracked live...i.e someone selected 2 of ticket_x with total of 5 available automatically drops quantityAvailable to 3 (i.e holding tickets)
}) => {
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [incrementCounter, setIncrementCounter] = useState(1);

  const addButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (!isEdit) {
      setIncrementCounter(1);
    }
  }, [isEdit]);

  useEffect(() => {
    if (ticketsPurchase.length > 0) {
      const t = ticketsPurchase
        .map((tp) => tp.quantity)
        .reduce((prev, curr, currIdx, arr) => prev + curr, 0);

      setTotalQuantity(t);
    }
    // eslint-disable-next-line
  }, [ticketsPurchase]);

  useEffect(() => {
    return () => {
      addButtonRefs.current = [];
    };
  }, []);

  return (
    <div className="flex w-full flex-col gap-3">
      {tickets
        .filter((ticket) => {
          if (editableView) {
            return ticketsPurchase
              .map((t) => t.ticket._id)
              .includes(ticket._id);
          }
          return true;
        })
        .map((ticket, idx) => {
          const quantityPurchased =
            ticketsPurchase.find((t) => t.ticket._id === ticket._id)
              ?.quantity ?? 0;

          const isPurchased =
            ticketsPurchase.find((t) => t.ticket._id === ticket._id) !==
            undefined;

          return (
            <div
              className={classNames(
                "flex gap-3 rounded-md border border-grey-200 py-3 pl-4 pr-3",
                "transition-all duration-300",
                {
                  "!border-[1.5px] !border-primary": isPurchased && !editableView,
                },
              )}
              key={ticket._id}
              onClick={() => {
                if (addButtonRefs.current[idx]) {
                  addButtonRefs.current[idx].click();
                }
              }}
            >
              <div className="flex flex-1 flex-col gap-1">
                <p className="font-onest text-sm capitalize leading-5 text-grey-700">
                  {editableView
                    ? `${quantityPurchased}x ${ticket.name}`
                    : ticket.name}
                </p>
                <p className="font-onest text-base font-medium leading-6 text-grey-950">
                  {ticket.ticketType === "free"
                    ? "FREE"
                    : formatToCurrency(ticket.price, {
                        currency: getCurrencyShort(ticket.currency ?? "NGN"),
                      })}
                </p>
                {ticket.description ? (
                  <div className="font-onest text-sm leading-5 text-grey-700">
                    <RenderMarkdown>{ticket.description}</RenderMarkdown>
                  </div>
                ) : undefined}
              </div>

              {ticket.quantityType === "limited" &&
              ticket.quantityAvailable === 0 &&
              !editableView ? (
                <Pill text="SOLD OUT" variation="red" />
              ) : (
                <div className="flex h-fit w-fit items-center">
                  {ticket.quantityType === "limited" &&
                  ((editableView && isEdit) || !editableView) &&
                  ticket.quantityAvailable - quantityPurchased <=
                    WARNING_TICKET_QUANTITY ? (
                    <p className="mr-2 text-sm font-bold text-success">
                      {ticket.quantityAvailable}{" "}
                      {ticket.quantityAvailable === 1 ? "ticket " : "tickets "}
                      left
                    </p>
                  ) : undefined}
                  {showControls && (
                    <>
                      <button
                        type="button"
                        disabled={quantityPurchased <= minPurchaseQuantity}
                        className={classNames(
                          "rounded-md border border-grey-200 p-1 text-primary disabled:cursor-not-allowed disabled:bg-grey-100 disabled:text-grey-300",
                          { hidden: !isPurchased },
                          "!w-fit",
                        )}
                        onClick={(e) => {
                          e.stopPropagation();

                          const purchaseCopy: TicketPurchase[] = JSON.parse(
                            JSON.stringify(ticketsPurchase),
                          );

                          const ticketIndex = purchaseCopy.findIndex(
                            (item) => item.ticket._id === ticket._id,
                          );

                          purchaseCopy[ticketIndex].quantity =
                            purchaseCopy[ticketIndex].quantity - 1;

                          if (purchaseCopy[ticketIndex].quantity === 0) {
                            setTicketsPurchase(
                              purchaseCopy.filter(
                                (t) => t.ticket._id !== ticket._id,
                              ),
                            );
                          } else {
                            setTicketsPurchase(purchaseCopy);
                          }
                        }}
                      >
                        <Icon
                          svg="minus"
                          width={20}
                          height={20}
                          className="min-w-fit"
                        />
                      </button>

                      {quantityPurchased > 0 && (
                        <p className="w-fit min-w-[28px] p-1 text-center font-bold transition-all duration-150">
                          {quantityPurchased}
                        </p>
                      )}

                      <button
                        ref={(el) => {
                          addButtonRefs.current[idx] = el;
                        }}
                        type="button"
                        disabled={
                          (ticket.quantityType === "limited" &&
                            (isEdit && liveQuantityTracking
                              ? ticket.quantityAvailable - incrementCounter < 0
                              : quantityPurchased >=
                                ticket.quantityAvailable)) ||
                          totalQuantity >= MAX_PURCHASE_QUANTITY
                        }
                        className={classNames(
                          "rounded-md border border-grey-200 p-1 text-primary disabled:cursor-not-allowed disabled:bg-grey-100 disabled:text-grey-300",
                          "!w-fit",
                        )}
                        onClick={(e) => {
                          e.stopPropagation();

                          if (isPurchased) {
                            const purchaseCopy: TicketPurchase[] = JSON.parse(
                              JSON.stringify(ticketsPurchase),
                            );

                            const ticketIndex = purchaseCopy.findIndex(
                              (item) => item.ticket._id === ticket._id,
                            );

                            purchaseCopy[ticketIndex].quantity =
                              purchaseCopy[ticketIndex].quantity + 1;

                            setTicketsPurchase(purchaseCopy);
                          } else {
                            const purchaseCopy: TicketPurchase[] = JSON.parse(
                              JSON.stringify(ticketsPurchase),
                            );

                            purchaseCopy.push({
                              ticket: ticket,
                              quantity: 1,
                            });

                            setTicketsPurchase(purchaseCopy);
                          }

                          setIncrementCounter((prev) => prev + 1);
                        }}
                      >
                        <Icon
                          svg="plus"
                          width={20}
                          height={20}
                          className="min-w-fit"
                        />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};
